import React, { useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { FiChevronDown, FiChevronUp, FiCopy } from 'react-icons/fi';
import { copyTextToClipboard } from '../../blockchain/offlineSigning';

const Wrap = styled.div`
  margin: 0 0 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.2);
`;

const Toggle = styled.button.attrs({ type: 'button' })`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: none;
  background: rgba(255, 255, 255, 0.04);
  color: #e2e8f0;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.07);
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
  }

  .badge {
    font-size: 0.72rem;
    font-weight: 600;
    padding: 0.15rem 0.45rem;
    border-radius: 6px;
    background: rgba(79, 172, 254, 0.15);
    color: #7dd3fc;
  }

  .badge.ref {
    background: rgba(251, 191, 36, 0.12);
    color: #fcd34d;
  }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0.35rem 0.75rem 0;
`;

const CopyBtn = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.65rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.06);
  color: #cbd5e1;
  font-size: 0.78rem;
  cursor: pointer;

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const Pre = styled.pre`
  margin: 0;
  max-height: min(42vh, 420px);
  overflow: auto;
  padding: 0.75rem 1rem 1rem;
  font-family: Consolas, Monaco, 'Courier New', monospace;
  font-size: 0.78rem;
  line-height: 1.45;
  color: #cbd5e1;
  white-space: pre;
  background: rgba(15, 15, 35, 0.85);
`;

const Note = styled.p`
  margin: 0;
  padding: 0 1rem 0.65rem;
  font-size: 0.78rem;
  line-height: 1.45;
  color: #94a3b8;
`;

/**
 * @param {{ title: string, fileName?: string, source: string, variant?: 'prebuilt' | 'reference', note?: string }} props
 */
export function ContractSoliditySourcePanel({ title, fileName, source, variant = 'prebuilt', note }) {
  const [open, setOpen] = useState(true);
  const text = String(source || '').trim();
  if (!text) return null;

  return (
    <Wrap>
      <Toggle type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="meta">
          <span>{title}</span>
          {fileName ? <span className="badge">{fileName}</span> : null}
          {variant === 'reference' ? <span className="badge ref">Reference only</span> : null}
        </span>
        {open ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
      </Toggle>
      {open ? (
        <>
          {note ? <Note>{note}</Note> : null}
          <Toolbar>
            <CopyBtn
              type="button"
              onClick={async () => {
                await copyTextToClipboard(text);
                toast.success('Solidity source copied');
              }}
            >
              <FiCopy size={14} /> Copy source
            </CopyBtn>
          </Toolbar>
          <Pre>{text}</Pre>
        </>
      ) : null}
    </Wrap>
  );
}

export default ContractSoliditySourcePanel;
